import axios from "axios";
import store from "../store/index";
import i18n from "../i18n.js";
import router from "../router";

const getPreviousPath = (fullPath) => {
  const pathArray = fullPath.split("/");
  return pathArray.slice(0, pathArray.length - 1).join("/");
};

/**
 * Displays toast or system alert depending on which error code is returned
 * @param {error} err Error response
 */
const handleHttpError = (err) => {
  let message;
  let type;
  let duration = 5000;
  let alertType = "system";

  if (err.message !== "User not found.") {
    if (err.message.includes("401") && location.pathname !== "/") {
      alertType = "none";
      localStorage.clear();
      window.location.assign("/");
    } else if (err.response.status === 400) {
      alertType = "toast";
    } else if (err.response.status === 403) {
      alertType = "toast";
      message = i18n.t("Client error: Forbidden.");
    } else if (err.response.status === 404) {
      alertType = "toast";
      router.push(getPreviousPath(router.history.current.fullPath));
    } else if (err.response.status === 405) {
      message = i18n.t("Client error: Method not allowed.");
    } else if (err.response.status === 408) {
      message = i18n.t("Client error: Request timed out.");
    } else if (err.response.status === 409) {
      alertType = "toast";
    } else if (err.response.status === 422) {
      alertType = "toast";
    } else if (err.response.status === 429) {
      message = i18n.t("Client error: Too many requests.");
    } else if (String(err.response.status).startsWith(50)) {
      alertType = "toast";
    } else if (err.toJSON().message === "Network Error") {
      message = i18n.t(
        "Connection lost. Please check your network and try again."
      );
      type = "no-connection";
    }
    if (alertType === "system")
      store.dispatch(
        "alert/displayAlert",
        {
          position: "top",
          variant: "danger",
          message,
          duration,
          type,
        },
        { root: true }
      );

    if (
      alertType === "toast" &&
      err.response !== undefined &&
      !err.response.data.error.includes("2FA is enabled") &&
      !err.response.data.error.includes("Incorrect code entered") &&
      !err.response.data.error.includes("Code required") &&
      !err.response.data.error.includes("Invalid code") &&
      !err.response.data.error.includes("2FA is enforced")
    )
      store.dispatch(
        "toast/displayToast",
        {
          title: i18n.t("Error"),
          message: err.response.data.error,
          variant: "danger",
          duration,
        },
        { root: true }
      );
  }
};

export default {
  install(Vue) {
    axios.defaults.withCredentials = true;

    Vue.prototype.$http = axios.create({
      baseURL: process.env.VUE_APP_API_URL,
    });

    /**
     * Attaches JWT as Bearer token in Authorization header to all subsequent requests.
     * @param {JWT} token JWT received from server
     */
    Vue.prototype.$http.setAuthorisationToken = function (token) {
      if (token) {
        this.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      } else {
        delete axios.defaults.headers.common["Authorization"];
      }
    };

    Vue.prototype.$http.interceptors.request.use(function (request) {
      if (
        request.url.match("/users/0") ||
        request.url.match("user_id=0") ||
        request.url.match("blockpage/0")
      ) {
        throw new axios.Cancel("User not found.");
      }
      return request;
    });

    /**
     * Intercepts HTTP responses and handles errors
     */
    Vue.prototype.$http.interceptors.response.use(
      (config) => {
        store.getters["alert/alert"]?.type === "no-connection" &&
          store.dispatch("alert/closeAlert");
        return config;
      },
      (err) => {
        handleHttpError(err);

        return Promise.reject(err);
      }
    );
  },
};
